import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Box, Card, FormControl, InputLabel, MenuItem, Select, CircularProgress, Divider } from '@mui/material'
import { PeopleAlt } from '@mui/icons-material';
import DocumentScannerOutlinedIcon from '@mui/icons-material/DocumentScannerOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import PieChartOutlineIcon from '@mui/icons-material/PieChartOutline';
import { AiOutlineScan } from 'react-icons/ai'
import StackedBarChart from '../components/StackedBarChart';
import DonutChart from '../components/DonutChart';
import MapComponent from '../components/Map';
import Dropdown from '../components/DropDown';
import DateRangePicker from '../components/DateRangePicker';
import { getCropsAndVarieties, getAllMachine, getScansCount, getScansDataUsingCrops, getUsersCount } from '../utils/api';
import { onGetFormatedDateRange } from '../utils/common';

const ACCENT_BLUE = 'rgb(34, 93, 153)';
const ACCENT_ORANGE = 'rgb(217, 108, 60)';
const ACCENT_GREEN = 'rgb(99, 179, 100)';

const cardShell = {
  borderRadius: '10px',
  boxShadow: '0 2px 10px rgba(12, 61, 110, 0.05)',
  border: '1px solid rgba(34, 93, 153, 0.06)',
  backgroundColor: '#fff',
}

const selectSx = {
  borderRadius: '10px',
  backgroundColor: '#fff',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(34, 93, 153, 0.15)' },
}

const CardHeader = ({ icon, title, accent }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: 2.5, py: 2 }}>
    <Box sx={{
      width: 34, height: 34, borderRadius: '10px',
      backgroundColor: `${accent}1A`, color: accent,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {icon}
    </Box>
    <p style={{ fontSize: 15, fontWeight: 700, color: 'rgb(25,25,35)', margin: 0 }}>{title}</p>
  </Box>
)

const ChartEmptyState = ({ text }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, color: 'rgb(170,170,180)' }}>
    <BarChartOutlinedIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
    <p style={{ fontSize: 13, margin: 0 }}>{text}</p>
  </Box>
)

const Home = () => {
  const company_id = localStorage.getItem("company_id");
  const home_redux = useSelector(state => state.home)
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(true)

  const [numberOfUsers, setNumberOfUsers] = useState(0)
  const [totalScansCount, setTotalScansCount] = useState(0)
  const [totalSubScansCount, setTotalSubScansCount] = useState(0)

  const [allCropVarieties, setAllCropVarieties] = useState([])
  const [uniqueCrops, setUniqueCrops] = useState([])
  const [selectedCropNames, setSelectedCropNames] = useState([])
  const [varietyOptions, setVarietyOptions] = useState([])
  const [selectedVarietyIds, setSelectedVarietyIds] = useState([])

  const [machineList, setMachineList] = useState([])
  const [selectedMachineIds, setSelectedMachineIds] = useState([])

  const [dateOption, setDateOption] = useState('this_week')
  const [isCustomDateSelected, setIsCustomDateSelected] = useState(false)
  const [customDateRange, setCustomDateRange] = useState([])
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')

  const [chartLoading, setChartLoading] = useState(false)
  const [chartCategories, setChartCategories] = useState([])
  const [chartSeries, setChartSeries] = useState([])
  const [mapData, setMapData] = useState([])

  const [gradeDistribution, setGradeDistribution] = useState({ series: [], labels: [] })

  useEffect(() => {
    (async () => {
      try {
        const hasCache = home_redux?.user_count && home_redux?.crop_list?.length && home_redux?.machine_list?.length

        if (hasCache) {
          const uniqueCropList = [...new Map(home_redux.crop_list.map(c => [c.crop_name, c])).values()]
          setUniqueCrops(uniqueCropList)
          setSelectedCropNames(uniqueCropList.map(c => c.crop_name))
          setAllCropVarieties(home_redux.crop_list)
          setVarietyOptions(home_redux.crop_list)
          setSelectedVarietyIds(home_redux.crop_list.map(v => v.variety_id))

          setMachineList(home_redux.machine_list)
          setSelectedMachineIds(home_redux.machine_list.map(m => m.machine_id))

          setNumberOfUsers(home_redux.user_count)
          setTotalScansCount(home_redux.user_scans_count)
          setTotalSubScansCount(home_redux.user_sub_scans_count)

          if (home_redux.home_crop_vs_scans_data?.crop_Categories?.length) {
            setChartCategories(home_redux.home_crop_vs_scans_data.crop_Categories)
            setChartSeries(home_redux.home_crop_vs_scans_data.serial_data)
          }
          if (home_redux.home_map_data?.length) {
            setMapData(home_redux.home_map_data)
            buildGradeDistribution(home_redux.home_map_data)
          }

          setLoading(false)
          return
        }

        const [userCount, scanCount, crops, machines] = await Promise.all([
          getUsersCount(company_id),
          getScansCount(company_id),
          getCropsAndVarieties(company_id),
          getAllMachine(company_id),
        ])
        const usersVal = userCount?.data?.data || 0
        const scansVal = scanCount?.data?.data?.scan_count || 0
        const subScansVal = scanCount?.data?.data?.sub_scan_count || 0

        setNumberOfUsers(usersVal)
        setTotalScansCount(scansVal)
        setTotalSubScansCount(subScansVal)

        const cropVarietyData = crops?.data?.data || []
        setAllCropVarieties(cropVarietyData)

        const uniqueCropList = [...new Map(cropVarietyData.map(c => [c.crop_name, c])).values()]
        setUniqueCrops(uniqueCropList)
        setSelectedCropNames(uniqueCropList.map(c => c.crop_name))

        setVarietyOptions(cropVarietyData)
        setSelectedVarietyIds(cropVarietyData.map(v => v.variety_id))

        const machineData = machines?.data?.data || []
        setMachineList(machineData)
        setSelectedMachineIds(machineData.map(m => m.machine_id))

        dispatch({
          type: 'user_count_success',
          payload: [usersVal, scansVal, subScansVal]
        })
        dispatch({
          type: 'crop_machine_data',
          payload: [cropVarietyData, machineData]
        })

        setLoading(false)
        fetchChartData(cropVarietyData, machineData.map(m => m.machine_id), 'this_week')
      } catch (err) {
        console.error(err)
        setLoading(false)
      }
    })()
  }, [])

  // Same pattern as UserDetails.jsx — DateRangePicker gives back a 2-item
  // array with .format() dayjs-like objects; convert that into ISO start/end strings.
  useEffect(() => {
    if (customDateRange && customDateRange.length === 2) {
      const startDate = new Date(customDateRange[0].format())
      const endDate = new Date(customDateRange[1].format())
      startDate.setHours(0, 0, 0, 0)
      endDate.setHours(23, 59, 59, 999)
      setCustomStart(startDate.toISOString())
      setCustomEnd(endDate.toISOString())
    }
  }, [customDateRange])

  const handleCropChange = (nextCropNames) => {
    setSelectedCropNames(nextCropNames)
    const varieties = allCropVarieties.filter(c => nextCropNames.includes(c.crop_name))
    setVarietyOptions(varieties)
    setSelectedVarietyIds(varieties.map(v => v.variety_id))
  }

  const handleDateOptionChange = (e) => {
    const selected = e.target.value
    setDateOption(selected)
    setIsCustomDateSelected(selected === 'custom')
  }

  const resolveDateRange = () => {
    if (dateOption === 'custom' && customStart && customEnd) {
      return { start_date: customStart, end_date: customEnd }
    }
    return onGetFormatedDateRange(dateOption)
  }

  const fetchChartData = async (varietyRows, machineIds, dateOpt) => {
    if (!varietyRows?.length || !machineIds?.length) return
    setChartLoading(true)
    try {
      const { start_date, end_date } = resolveDateRange()
      const combinations = varietyRows.flatMap(({ crop_id, variety_id }) =>
        machineIds.map((machine_id) => ({ crop_id, variety_id, machine_id }))
      )
      const res = await getScansDataUsingCrops({
        crop_variety_machine: combinations,
        start_date,
        end_date,
        company_id,
      })
      const data = res?.data?.data || []
      setMapData(data)
      const { categories, series } = buildCropChart(data)
      buildGradeDistribution(data)

      dispatch({ type: 'HOME_MAP_DATA', payload: data })
      dispatch({
        type: 'HOME_CROP_VS_SCAN_DATA',
        payload: [categories, series, { start_date, end_date }]
      })
    } catch (err) {
      console.error(err)
    } finally {
      setChartLoading(false)
    }
  }

  const GRADE_RANGES = [
    { name: 'Grade A', min: 90, max: 100 },
    { name: 'Grade B', min: 80, max: 90 },
    { name: 'Grade C', min: -1, max: 80 },
  ]

  const buildCropChart = (dataList) => {
    const groups = dataList.reduce((acc, item) => {
      const name = item.crop_name
      const group = acc.find(g => g.name === name)
      if (group) group.scores.push(item.overall_score)
      else acc.push({ name, scores: [item.overall_score] })
      return acc
    }, [])

    const series = GRADE_RANGES.map((grade) => ({
      name: grade.name,
      data: groups.map(g => g.scores.filter(s => s > grade.min && s <= grade.max).length),
    }))
    const categories = groups.map(g => g.name)

    setChartCategories(categories)
    setChartSeries(series)
    return { categories, series }
  }

  const buildGradeDistribution = (dataList) => {
    const totals = { 'Grade A': 0, 'Grade B': 0, 'Grade C': 0 }
    dataList.forEach((item) => {
      const s = item.overall_score
      if (s == null) return
      if (s > 90 && s <= 100) totals['Grade A']++
      else if (s > 80 && s <= 90) totals['Grade B']++
      else totals['Grade C']++
    })
    setGradeDistribution({
      labels: Object.keys(totals),
      series: Object.values(totals),
    })
  }

  const handleViewData = () => {
    const rows = varietyOptions.filter(v => selectedVarietyIds.includes(v.variety_id))
    fetchChartData(rows, selectedMachineIds, dateOption)
  }

  const statCards = [
    { icon: <PeopleAlt sx={{ fontSize: 24 }} />, label: 'Number Of Users', value: numberOfUsers, accent: ACCENT_BLUE },
    { icon: <AiOutlineScan size={24} />, label: 'Number Of Scans', value: totalScansCount, accent: ACCENT_ORANGE },
    { icon: <DocumentScannerOutlinedIcon sx={{ fontSize: 24 }} />, label: 'Number Of Sub-Scans', value: totalSubScansCount, accent: ACCENT_GREEN },
  ]

  const hasGradeData = gradeDistribution.series.length > 0 && gradeDistribution.series.some(v => v > 0)

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress sx={{ color: ACCENT_ORANGE }} />
      </Box>
    )
  }

  return (
    <Box sx={{ px: 3, backgroundColor: '#eef4fb', minHeight: '100vh' }}>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '18px', mb: 3 }}>
        {statCards.map((card, i) => (
          <Card
            key={i}
            sx={{
              ...cardShell,
              flex: '1 1 260px',
              p: '20px 22px',
              position: 'relative',
              overflow: 'hidden',
              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              '&:hover': { boxShadow: '0 10px 28px rgba(12, 61, 110, 0.10)', transform: 'translateY(-2px)' },
            }}
          >
            <Box sx={{
              position: 'absolute', top: -20, right: -20, width: 100, height: 100,
              borderRadius: '50%', backgroundColor: `${card.accent}0D`,
            }} />
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Box>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'rgb(150,150,160)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{card.label}</p>
                <p style={{ fontSize: 30, fontWeight: 800, color: 'rgb(20,20,30)', margin: '6px 0 0', lineHeight: 1 }}>{card.value.toLocaleString()}</p>
              </Box>
              <Box sx={{
                width: 46, height: 46, borderRadius: '13px',
                backgroundColor: card.accent, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 6px 16px ${card.accent}4D`,
              }}>
                {card.icon}
              </Box>
            </Box>
          </Card>
        ))}
      </Box>

      <Card sx={{ ...cardShell, p: '22px', mb: 3 }}>
        {/* Single row — nowrap keeps everything on one line; overflow-x lets it scroll on narrow screens instead of wrapping/squishing */}
        <Box sx={{
          display: 'flex',
          flexWrap: 'nowrap',
          gap: '12px',
          alignItems: 'center',
          overflowX: 'auto',
          pt: 1.5,
          pb: 1,
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(34, 93, 153, 0.25)', borderRadius: 3 },
        }}>

          <Box sx={{ flexShrink: 1, minWidth: 0 }}>
            <Dropdown
              label="Crop"
              minWidth={isCustomDateSelected ? 150 : 200}
              options={uniqueCrops}
              getKey={(c) => c.crop_name}
              getLabel={(c) => c.crop_name}
              selectedKeys={selectedCropNames}
              onChange={handleCropChange}
              allLabel="All crops"
            />
          </Box>

          <Box sx={{ flexShrink: 1, minWidth: 0 }}>
            <Dropdown
              label="Variety"
              minWidth={isCustomDateSelected ? 170 : 240}
              options={varietyOptions}
              getKey={(v) => v.variety_id}
              getLabel={(v) => v.variety_name}
              getChipLabel={(v) => `${v.crop_name}: ${v.variety_name}`}
              getGroupLabel={(v) => v.crop_name}
              selectedKeys={selectedVarietyIds}
              onChange={setSelectedVarietyIds}
              allLabel="All varieties"
            />
          </Box>

          <Box sx={{ flexShrink: 1, minWidth: 0 }}>
            <Dropdown
              label="Machine"
              minWidth={isCustomDateSelected ? 150 : 220}
              options={machineList}
              getKey={(m) => m.machine_id}
              getLabel={(m) => m.machine_name}
              selectedKeys={selectedMachineIds}
              onChange={setSelectedMachineIds}
              allLabel="All machines"
            />
          </Box>

          <FormControl size="small" sx={{ minWidth: isCustomDateSelected ? 130 : 160, flexShrink: 1 }}>
            <InputLabel>Date range</InputLabel>
            <Select label="Date range" value={dateOption} onChange={handleDateOptionChange} sx={selectSx}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="this_week">This Week</MenuItem>
              <MenuItem value="this_month">This Month</MenuItem>
              <MenuItem value="this_year">This Year</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </FormControl>

          {isCustomDateSelected && (
            <Box sx={{ flexShrink: 0 }}>
              <DateRangePicker setDateRange={setCustomDateRange} />
            </Box>
          )}

          <button
            onClick={handleViewData}
            style={{
              background: `linear-gradient(135deg, ${ACCENT_BLUE}, rgb(24, 73, 123))`,
              color: '#fff', border: 'none', borderRadius: '10px',
              padding: '10px 26px', fontWeight: 700, fontSize: 13.5, cursor: 'pointer',
              marginLeft: 'auto', flexShrink: 0, whiteSpace: 'nowrap',
              boxShadow: '0 6px 16px rgba(34, 93, 153, 0.3)',
            }}
          >
            View Data
          </button>
        </Box>
      </Card>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '18px', mb: 3 }}>
        <Card sx={{ ...cardShell, flex: '1 1 420px', overflow: 'hidden' }}>
          <CardHeader icon={<BarChartOutlinedIcon sx={{ fontSize: 19 }} />} title="Crop vs Scans" accent={ACCENT_BLUE} />
          <Divider />
          <Box sx={{ p: 2 }}>
            {chartLoading
              ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: ACCENT_ORANGE }} size={28} /></Box>
              : chartSeries.length > 0
                ? <StackedBarChart title="" seriesData={chartSeries} categoriesData={chartCategories} xaxis_title="Crops" yaxis_title="Number Of Scans" />
                : <ChartEmptyState text="Select filters and click View Data to see results" />}
          </Box>
        </Card>

        <Card sx={{ ...cardShell, flex: '1 1 420px', overflow: 'hidden' }}>
          <CardHeader icon={<PieChartOutlineIcon sx={{ fontSize: 19 }} />} title="Grade Distribution" accent={ACCENT_ORANGE} />
          <Divider />
          <Box sx={{ p: 2 }}>
            {chartLoading
              ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: ACCENT_ORANGE }} size={28} /></Box>
              : hasGradeData
                ? <DonutChart series={gradeDistribution.series} labels={gradeDistribution.labels} title="Total Scans" />
                : <ChartEmptyState text="Select filters and click View Data to see results" />}
          </Box>
        </Card>
      </Box>

      {mapData.length > 0 && (
        <Card sx={{ ...cardShell, overflow: 'hidden' }}>
          <CardHeader icon={<PlaceOutlinedIcon sx={{ fontSize: 19 }} />} title="Scans vs Locations" accent={ACCENT_GREEN} />
          <Divider />
          <Box sx={{ p: 2 }}>
            <MapComponent dataArray={mapData} />
          </Box>
        </Card>
      )}
    </Box>
  )
}

export default Home